import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import Lightbox from '../components/Lightbox';
import Picture from '../components/Picture';
import SEO from '../components/SEO';

const HERO_IMG = '/images/committee.jpg';

const performers = [
  { src: '/images/heritage-jayne-darling.jpg', caption: 'Jayne Darling', position: 'top' },
  { src: '/images/heritage-malcolm-music-piper.jpg', caption: 'Malcolm Music Lone Piper' },
  { src: '/images/heritage-diggle-brass.jpg', caption: 'Diggle Brass Band' },
  { src: '/images/heritage-fab-choir.jpg', caption: 'Fab Choir' },
  { src: '/images/heritage-natalie-ciufo.jpg', caption: 'Natalie Ciufo', position: 'top' },
  { src: '/images/heritage-manchester-pipe-band.jpg', caption: 'Manchester Pipe Band' },
  { src: '/images/heritage-rachel-mercer.jpg', caption: 'Rachel Mercer' },
  { src: '/images/heritage-chapel-male-voice-choir.jpg', caption: 'Chapel-en-le-Frith Male Voice Choir' },
  { src: '/images/heritage-stone-the-crows.jpg', caption: 'Stone the Crows Morris Dancers' },
  { src: '/images/heritage-milltown-cloggies.jpg', caption: 'Milltown Cloggies' },
  { src: '/images/heritage-soiree.jpg', caption: 'Soiree' },
  { src: '/images/heritage-dark-peak-music.jpg', caption: 'Dark Peak Music Foundation' },
  { src: '/images/heritage-glossop-old-band.jpg', caption: 'Glossop Old Band' },
  { src: '/images/heritage-tintwistle-brass.jpg', caption: 'Tintwistle Brass Band' },
  { src: '/images/heritage-burbage-band.jpg', caption: 'Burbage Band' },
  { src: '/images/heritage-marple-brass.jpg', caption: 'Marple Brass Band' },
  { src: '/images/heritage-chicago-blues-brothers.jpg', caption: 'Chicago Blues Brothers' },
  { src: '/images/heritage-lah-di-dah-girls.jpg', caption: 'The Lah-di-dah Girls' },
  { src: '/images/heritage-kyle-evans-formby.jpg', caption: 'Kyle Evans — George Formby Tribute' },
];

const allImages = performers.map((p) => p.src);
const allCaptions = performers.map((p) => p.caption);

export default function HeritageProgrammePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const totalImages = allImages.length;

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) => (i <= 0 ? totalImages - 1 : i - 1));
  }, [totalImages]);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) => (i >= totalImages - 1 ? 0 : i + 1));
  }, [totalImages]);

  return (
    <div data-testid="heritage-programme-page">
      <SEO
        title="Glossop Heritage Weekend 2026 — Programme"
        description="Glossop Heritage Weekend 2026 — free 3-day event 29th-31st May. Parades, traction engines, brass bands, Morris dancers, beer festival, artisan food, live entertainment in Norfolk Square, Glossop."
        path="/heritage-programme"
        keywords={['Glossop Heritage Weekend', 'Glossop Heritage Weekend 2026', 'Glossop Victorian Weekend', 'heritage weekend Derbyshire', 'Norfolk Square Glossop', 'free events Glossop', 'Glossop parade', 'brass bands Glossop']}
      />
      {lightboxIndex !== null && (
        <Lightbox
          images={allImages}
          captions={allCaptions}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}

      <HeroSection
        image={HERO_IMG}
        title="Heritage Weekend 2026"
        subtitle="Free to attend – 29th, 30th & 31st May 2026"
        fullHeight={false}
      />

      {/* Programme */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">Programme</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Heritage Weekend 2026
            </h2>
          </div>

          <div className="space-y-16">
            {/* Friday */}
            <article data-testid="heritage-friday" className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500]">Day 1</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0A0A0A]">
                Friday 29th May 2026 — 18:00 to 22:45
              </h3>
              <div className="space-y-3 text-base text-[#52525B] leading-relaxed">
                <p>
                  <strong className="text-[#0A0A0A]">18:00</strong> Local Craft Beer Tent with over 30 real ales, Gin &amp; Cocktail Bar &amp; Fair Rides open. Local artisan food stalls with delicious BBQs and nibbles.
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">18:00</strong> On Stage: Live entertainment with period music, singers and dancing. Guaranteed to get your feet tapping!
                </p>
                <p className="text-sm italic">Please buy a programme for full details nearer the time. Close 22:45.</p>
              </div>
            </article>

            <hr className="border-zinc-200" />

            {/* Saturday */}
            <article data-testid="heritage-saturday" className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500]">Day 2</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0A0A0A]">
                Saturday 30th May 2026 — 10:00 to 23:00
              </h3>
              <div className="space-y-3 text-base text-[#52525B] leading-relaxed">
                <p>
                  <strong className="text-[#0A0A0A]">09:30</strong> For less mobile, or folk that cannot manage the crowds, at Bradbury Community House, Market St, free tea &amp; cake and entertainment before the parade; and again at 14:00 a matinee. Blue badge holders park on Chapel St.
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">10:30</strong> Parade assembles outside GNE Football Club, Surrey St, Glossop, SK13 7AG.
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">11:00</strong> Parade starts led by a Pipe Band, Traction engines, Shire Horses, Re-enactors, Morris dancers, Brass Bands, Floats, Vintage vehicles and so much more!
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">12:00</strong> On Stage Norfolk Square: Dep Lord Lieutenant Opens Event. Best Fancy Dress (adult and child), Best Shop and Float announcements.
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">12:10</strong> On Stage: Heritage live entertainment, with bands, singers and dancers. Craft artisan market and food, Real Ale, Craft Gin and cocktail bar throughout. Lots of static displays, Traction engines, Shire horses, Re-enactment folk, Vintage vehicles — kids entertainment, fairground, circus skills &amp; so much more.
                </p>
                <p>
                  A special visit from our chosen charity the Veteran's living history museum and range plus swing dancing on the streets, a mini steam train ride and much much more.
                </p>
                <p className="text-sm italic">
                  Please buy a souvenir programme nearer the time for full details (all proceeds go to provide this free not for profit event). Close 23:00.
                </p>
              </div>
            </article>

            <hr className="border-zinc-200" />

            {/* Sunday */}
            <article data-testid="heritage-sunday" className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500]">Day 3</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0A0A0A]">
                Sunday 31st May 2026 — 09:45 to 17:00
              </h3>
              <div className="space-y-3 text-base text-[#52525B] leading-relaxed">
                <p>
                  Live music and dancing, entertainers, Brass bands, Morris dancers. Displays of Traction engines, Shire horses, Re-enactors, Vintage vehicles, artisan market, and so much more — kids entertainment and vintage fairground all afternoon.
                </p>
                <p>Craft beer, gin and cocktail bar, artisan food and crafts.</p>
                <p>
                  <strong className="text-[#0A0A0A]">09:45–10:30</strong> Multi-faith Church Service
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">10:30–10:50</strong> Malcolm Music Lone Piper
                </p>
                <p>
                  A special exhibition at Bradbury House from our friends at Glossop Heritage Trust noon – 15:30 with two illustrated talks at 12:30 and 14:30 "Inventions and innovations in Glossop".
                </p>
                <p className="font-semibold text-[#0A0A0A]">
                  Then on stage and around the event — family fun and live displays, events and a packed list of entertainment on stage until:
                </p>
                <p>
                  <strong className="text-[#0A0A0A]">17:00</strong> God Save the King. Thank you and CLOSE – Cllr Ed Siddall
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Performers Gallery */}
      <section data-testid="heritage-performers-gallery" className="py-20 sm:py-28 bg-[#F4F4F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">Featuring</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Performers &amp; Entertainment
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {performers.slice(0, -3).map((performer, i) => (
              <button
                key={performer.src}
                data-testid={`heritage-gallery-image-${i}`}
                onClick={() => openLightbox(i)}
                className="overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#F0A500] relative"
              >
                <Picture
                  src={performer.src}
                  alt={performer.caption}
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  style={performer.position === 'top' ? { objectPosition: 'center top' } : undefined}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                  <span className="text-white text-xs font-semibold px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity truncate w-full">
                    {performer.caption}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-3 mt-3">
            {performers.slice(-3).map((performer, i) => (
              <button
                key={performer.src}
                data-testid={`heritage-gallery-image-${performers.length - 3 + i}`}
                onClick={() => openLightbox(performers.length - 3 + i)}
                className="overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#F0A500] relative w-full max-w-[calc(25%-9px)]"
              >
                <Picture
                  src={performer.src}
                  alt={performer.caption}
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  style={performer.position === 'top' ? { objectPosition: 'center top' } : undefined}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                  <span className="text-white text-xs font-semibold px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity truncate w-full">
                    {performer.caption}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[#0A0A0A] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Want to Get Involved?
          </h2>
          <p className="text-base text-zinc-400 mb-8">
            If you would like to help marshal the event or get involved with Round Table, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:volunteers@glossoproundtable.org"
              data-testid="heritage-volunteer-btn"
              className="btn-amber inline-block text-center"
            >
              Volunteer
            </a>
            <Link
              to="/events"
              data-testid="heritage-back-events-btn"
              className="inline-block px-8 py-3 border border-white/30 text-white font-bold uppercase tracking-wide text-sm hover:bg-white/10 transition-all text-center"
            >
              Back to Events
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
